/**
 * "Click twice to confirm" for actions that throw something away (delete a preset, open a
 * project over unsaved changes). The first click *arms* a key; a second click on the same key
 * within a few seconds runs the action. Arming another key, or waiting, disarms.
 *
 * {@link ArmedButton} (`ui.tsx`) is the ready-made button for a single action; this is the
 * primitive for lists, where many buttons share one armed slot.
 */
import { createSignal, onCleanup, type Accessor } from "solid-js";

/** One armed slot. */
export interface Arming {
  /** The armed key, or `null`. */
  armed: Accessor<string | null>;
  /**
   * Runs `action` if `key` is armed (or if `needed` is `false`); otherwise arms `key`.
   * Returns `true` when the action ran.
   */
  confirm(key: string, action: () => void, needed?: boolean): boolean;
}

/** How long a key stays armed. */
const ARMED_FOR_MS = 3500;

/** Creates an armed slot. Call inside a component (the timer is cleared on unmount). */
export function createArming(): Arming {
  const [armed, setArmed] = createSignal<string | null>(null);
  let timer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(timer));
  return {
    armed,
    confirm(key, action, needed = true) {
      if (!needed || armed() === key) {
        clearTimeout(timer);
        setArmed(null);
        action();
        return true;
      }
      setArmed(key);
      clearTimeout(timer);
      timer = setTimeout(() => setArmed(null), ARMED_FOR_MS);
      return false;
    },
  };
}
